<template lang="pug">
  .swatches-container(:class="['swatches-'+swatchWidth, 'swatches-'+displayType]")
    p.label(v-if="label")
      span(v-translate v-html="label")
      span.option-count(v-if="displayType !== 'text'") &nbsp;({{options.length}})
      span.required(v-if="required") *
      span.val(v-if="selectedLabel && displayType !== 'text'") {{selectedLabel}}
      span.old-price(v-if="showRetailPrice")
        | {{$rogue.currency(selectedOption.retailPrice)}}
    .swatches
      .swatch-holder(v-for="(option, i) in filteredOptions" :data-code="code" :data-val="option.value || 'none'" :key="option.value" :data-key="code+'-'+(option.value || option.metaTitle || option.label || i)")
        component.swatch.a11y(
          :is="option.link ? 'RogueLink' : 'button'"
          :to="option.link"
          :class="[modelValue === option.value ? 'active' : '', option.status || '', option.disabled && 'disabled']"
          :data-value="(option.value + '').toLowerCase().replace(/[^a-z0-9]+/gi, '_')"
          :style="{'background-image':displayType === 'image' && 'url('+getSizedImage(option.image)+')'}"
          :aria-label="__(option.metaTitle || option.label)"
          :product-id="option.productId || null"
          :title="__(option.metaTitle || option.label)"
          @click="click($event, option)"
        )
          .swatch-text(v-if="displayType === 'text'") {{ __(option.label) }}
          .swatch-svg(v-if="displayType === 'svg'" v-html="option.svg")
          .size(v-else)
          AttributeSwatch.attr(v-if="displayType === 'attribute'" :code="code" :value="option.value" :colors="colors")
          .swatch-border
    RogueButton.see-more(
      v-if="filteredOptions.length < options.length || seeMore"
      size="s"
      flat="true"
      width="full"
      color="white"
      @click="seeMore = !seeMore")
      .button-text
        | {{seeMore ? __('See Less') : __('See More')}}
        Icon.icon(:name="seeMore ? 'arrow-up' : 'arrow-down'")
</template>
<script setup lang="ts">
import { defineAsyncComponent, ref, computed } from 'vue'
import { getCloudinaryUrl } from '../../js/helpers'


const AttributeSwatch = defineAsyncComponent(() => import(/* webpackChunkName: "sisu-attribute-swatch" */'../core/AttributeSwatch.vue'))

type SwatchOption = {
  value: number | string | null
  label?: string | number
  status?: string
  imgAttr?: boolean
  svg?: string
  image?: string
  link?: string
  productId?: string | number
  price?: number
  retailPrice?: number
  showStrikethroughPrice?: boolean
}

type Props = {
  options?: SwatchOption[]
  maxRows?: number
  modelValue?: number | string | null
  code?: string | null
  id?: string | null
  name?: string | null
  label?: string | null
  layout?: string
  required?: boolean
  colors?: Record<string, any> | undefined
  customWidth?: number
  customHeight?: number
  'onUpdate:modelValue'?: ((val: any) => void) | Array<(val: any) => void>
}

const props = withDefaults(defineProps<Props>(), {
  options      : () => [],
  maxRows      : 0,
  modelValue   : null,
  code         : null,
  id           : null,
  name         : null,
  label        : null,
  layout       : undefined,
  required     : false,
  colors       : undefined,
  customWidth  : 100,
  customHeight : 100,
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: number | string | null): void
  (e: 'clickSame', value: number | string | null): void
}>()

const seeMore = ref<boolean>(false)

const selectedOption = computed<SwatchOption>(() => props.options.find((opt) => opt.value === props.modelValue) || ({} as SwatchOption))
const selectedLabel = computed<string | undefined>(() => selectedOption.value.label)

const displayType = computed<'text' | 'attribute' | 'svg' | 'image'>(() => {
  if (props.layout === 'single-row') {
    return 'text'
  }

  if (props.options.every(opt => opt.imgAttr)) {
    return 'attribute'
  }
  if (props.options.every(opt => opt.svg)) {
    return 'svg'
  }
  if (props.options.every(opt => (opt.image || '').length > 0)) {
    return 'image'
  }

  return 'text'
})

const swatchWidth = computed<'one-line' | 'fixed' | 'thirds' | 'fit'>(() => {
  if (props.layout === 'single-row') {
    return 'one-line'
  }
  if (displayType.value !== 'text') {
    return 'fixed'
  }
  const maxLength = Math.max(...props.options.map((a) => ((a.label || '').trim()).length))

  if (props.options.length < 3) {
    return 'one-line'
  }

  if (maxLength <= 5) {
    return 'fixed'
  }

  if (props.options.length === 3 || maxLength > 20) {
    return 'thirds'
  }

  return 'fit'
})

const filteredOptions = computed<SwatchOption[]>(() => {
  if (seeMore.value || props.maxRows === 0) {
    return props.options
  }

  if (displayType.value === 'image') {
    return props.options.slice(0, 6 * (props.maxRows || 0))
  }

  return seeMore.value ? props.options : props.options.slice()
})

const showRetailPrice = computed<boolean>(() => {
  const so = selectedOption.value as any

  // sometimes we are including the price for a bundle option, and sometimes we're not. We only want to display the strikethrough if a price is being displayed.
  return so.price && so.retailPrice && so.price < so.retailPrice && so.showStrikethroughPrice && so.label.includes('(+')
})

function getSizedImage(img: any): string {
  let link = img
  if (typeof img === 'object' && img?.src) {
    link = img.src
  }
  if (String(link).indexOf('blob') === 0) {
    return link
  }

  return getCloudinaryUrl(link, props.customWidth, props.customHeight, '#ffffff', undefined as any)
}

function click(e: Event, option: SwatchOption) {
  if (!option.link && (props as any)['onUpdate:modelValue']) {
    e.preventDefault()
  }

  if (option.value !== props.modelValue) {
    emit('update:modelValue', option.value)
  } else {
    emit('clickSame', option.value)
  }
}
</script>
<style lang="scss" scoped>
.swatches-container {
  margin-bottom: $space-xxs;
}

label, .label {
  font-size: $size-xxs;
  display: block;
  font-family: $font-base;
  margin-bottom: $space-xxs;
}

.required {
  margin-left: 1px;
  color: $color-red;
}

.val {
  font-weight: $weight-bold;
  margin-left: $space-xxs;
}

.old-price {
  color: $color-gray-dark;
  margin-left: $space-xxs;
  text-decoration: line-through;
}

.swatch {
  font-family: $font-base;
  font-weight: $weight-bold;
  display: flex;
  width: 100%;
  line-height: $line-height-s;
  font-size: $size-xs;
  border-radius: 1px;
  align-items: center;
  text-align: center;
  background: no-repeat center center;
  justify-content: center;
  cursor: pointer;
  position: relative;
  margin: 0 0 $space-xxs $space-xxs;

  // 8px to the OUTSIDE of the border = 7px padding
  padding: $space-s 7px;

  &:hover {
    .swatch-border {
      border: $border-width-medium solid $color-gray-dark;
    }
  }
}

.swatch-border {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border: $border-width-thin solid $color-gray-light;
}

.swatches {
  display: flex;
  align-items: stretch;
  margin-left: -$space-xxs;
  flex-wrap: wrap;
}

.swatches-container {
  &.wheels {
    .swatches {
      flex-direction: row;
      flex-wrap: nowrap;
      gap: 12px;

      @include not-mobile() {
        button.swatch {
          margin: 0;
        }
      }
    }
  }
}

.swatch-holder {
  display: flex;
  align-items: stretch;
}

.swatches-text {
  .swatch.active {
    background-color: $color-black;
    color: $color-white;

    .swatch-border {
      border: none;
    }
  }

  .swatch.bin, .swatch.oos, .swatch.disabled {
    color: $color-gray-dark;

    &:hover {
      color: $color-black;
    }

    &.active {
      color: $color-white;
    }

    &.disabled {
      pointer-events: none;
    }
  }
}

[data-attr='rig_length'] {
  .swatches-fixed {
    .swatches {
      flex-wrap: nowrap;

      .swatch-holder:nth-of-type(1),
      .swatch-holder:nth-of-type(2) {
        width: 25%;
      }

      .swatch-holder:nth-of-type(3),
      .swatch-holder:nth-of-type(4) {
        width: 35%;
      }

      .swatch {
        font-size: 50px;
      }
    }
  }
}

// Layouts
.swatches-one-line {
  @include mobile() {
    .swatch-holder {
      width: 50%;
    }
  }

  @include not-mobile() {
    align-items: center;

    .swatches {
      margin-left: 0;
    }

    label, .label {
      font-size: $size-xxs;
    }

    .swatch-holder {
      width: 50%;
    }
  }
}

.swatches-thirds {
  .swatch-holder {
    width: 33.33%
  }
}

// Fixed swatches are all the same width
.swatches-fixed {
  .swatches {
    flex-wrap: wrap;
  }

  .swatch-holder {
    width: 25%;
  }

  .swatch {
    padding-left: 0;
    padding-right: 0;
  }

  @media (min-width: calc(32px + (4 * 100px))) {
    .swatch-holder {
      width: 20%
    }
  }
  @media (min-width: calc(32px + (5 * 100px))) {
    .swatch-holder {
      width: 16.6%
    }
  }
  @media (min-width: calc(32px + (6 * 100px))) {
    .swatch-holder {
      width: 14.25%
    }
  }
  @media (min-width: calc(32px + (7 * 100px))) {
    .swatch-holder {
      width: 12.5%
    }
  }
  @media (min-width: calc(32px + (8 * 100px))) {
    .swatch-holder {
      width: 11.1%
    }
  }
  @include all-desktop() {
    .swatch-holder {
      width: 16.6%
    }
  }
}

.swatches-fit {
  .swatches {
    flex-wrap: wrap;

    &::after {
      content: "";
      flex-grow: 999;
    }
  }

  .swatch-holder {
    max-width: 40%;
    min-width: 16.66%;
    flex-grow: 1;
  }
}

.swatches-attribute {
  .attr {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
  }

  .swatch {
    padding: 0;

    &.active .swatch-border {
      border: $border-width-medium solid $color-black;
    }

    .size {
      padding-top: 100%;
    }
  }
}
.swatches-svg {
  .swatch {
    padding: 0;
    &.active .swatch-border {
      border: $border-width-medium solid $color-gray-dark;
    }
  }
  .swatch-svg {
    :deep(svg) {
      width: 85%;
    }
  }
}

.swatches-image {
  .swatch {
    background-clip: content-box;
    background-size: contain;
    background-position: center center;
    background-repeat: no-repeat;
    padding: 0;

    .size {
      padding-top: 100%;
    }

    &.active .swatch-border {
      border: $border-width-medium solid $color-black;
    }
  }
}

.button-text {
  text-transform: uppercase;
  display: flex;
  font-weight: $weight-bold;
  justify-content: center;
  align-items: center;

  .icon {
    margin-left: $space-xxs; //@TODO incorrectly sized icon...
  }
}

.micro-swatch{
  margin-top: 0;
  margin-bottom: 0;

  .label{
    display: none;
  }

  .swatch-holder{
    width: 33%;

    .swatch:has(.swatch-text){
      padding: 7px 7px;
    }
  }

  .swatches-attribute{
    .swatch-holder[data-code="color"] {
      .swatch{
        padding: 0;
      }
    }
  }

  .attr-swatch{
    min-width: inherit;
  }

  @include mobile(){
    
    .swatches {
      display: flex;
      margin-bottom: 0;
    }

    .swatch{
      padding: 10px 7px;
    }

    .swatch-holder[data-code="color"] {
      .swatch{
        padding: 0;
      }
    }
  }

  @include tablet(){
    .swatch-holder{
      max-width: 48px;
    }
  }

  @include not-mobile(){
    .swatch-holder{
      width: 30%;
      min-width: inherit;
      max-width: inherit;
    }
  }

  @include all-desktop() {
    .swatch-holder{
      width: 33.33%;
      min-width: 37px;
    }
  }

  @media (min-width: 1280px) and (max-width: $largeBp) {
    .swatch-holder{
      width: 25%;
    }
  }

  @include xl() {
    .swatch-holder{
      width: 20%;
    }
  }
}

.micro-swatch.swatches-one-line{

  @include not-mobile() {
    align-items: center;

    .swatches {
      margin-left: 0;
    }

    label, .label {
      font-size: $size-xxs;
    }

    .swatch-holder {
      width: 50%;
    }
  }
}

.micro-swatch.swatches-container{
  margin-bottom: $space-xxxs;
}

</style>
